/**
 * Ingredient-aware conversion: the one door every entered quantity goes through on its way to
 * an ingredient's base unit (g, ml or each), in the API and in the browser alike.
 *
 * - Same kind (cups to millilitres, pounds to grams): the catalog factor.
 * - Mass and volume: the ingredient's density_g_per_ml ("1 cup of honey weighs 340 g").
 * - Count and mass: the ingredient's each_weight_g ("one dough ball weighs 280 g").
 * - Count and volume: both.
 * - Packages ("28 oz can"): the package's own quantity and unit, converted the same way.
 *
 * When a bridge is missing the conversion throws a ConversionError whose message tells the
 * owner exactly what to set, e.g. "Set how much 1 cup of Honey weighs".
 */

import {
  UNIT_CATALOG,
  UNIT_SYSTEMS,
  getUnit,
  type UnitDefinition,
  type UnitKind,
  type UnitSystem,
} from './catalog.ts';
import { UnitConversionError, kindOfBaseUnit, type BaseUnit } from './units.ts';

/** Postgres numeric columns arrive as strings; both forms are accepted. */
export type NumericInput = number | string;

export interface IngredientPackage {
  id: string;
  label: string;
  quantity: NumericInput;
  unit: string;
}

/** The fields conversion needs. API rows (snake_case) satisfy this directly. */
export interface ConvertibleIngredient {
  name?: string | null;
  base_unit: BaseUnit;
  density_g_per_ml?: NumericInput | null;
  each_weight_g?: NumericInput | null;
  packages?: readonly IngredientPackage[] | null;
}

/** A unit code from the catalog, or a reference to one of the ingredient's packages. */
export type QuantityUnit = string | { packageId: string };

export type ConversionErrorCode =
  | 'INVALID_QUANTITY'
  | 'UNKNOWN_UNIT'
  | 'UNKNOWN_PACKAGE'
  | 'DENSITY_REQUIRED'
  | 'EACH_WEIGHT_REQUIRED';

export class ConversionError extends UnitConversionError {
  readonly code: ConversionErrorCode;
  /** The ingredient field the owner must set for this conversion to work, if any. */
  readonly missing: 'density_g_per_ml' | 'each_weight_g' | null;
  readonly unit: string | null;
  constructor(
    code: ConversionErrorCode,
    message: string,
    missing: 'density_g_per_ml' | 'each_weight_g' | null = null,
    unit: string | null = null,
  ) {
    super(message);
    this.name = 'ConversionError';
    this.code = code;
    this.missing = missing;
    this.unit = unit;
  }
}

function positive(value: NumericInput | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function densityOf(ingredient: ConvertibleIngredient): number | null {
  return positive(ingredient.density_g_per_ml);
}

export function eachWeightOf(ingredient: ConvertibleIngredient): number | null {
  return positive(ingredient.each_weight_g);
}

function nameOf(ingredient: ConvertibleIngredient): string {
  return ingredient.name?.trim() || 'this ingredient';
}

function densityRequired(ingredient: ConvertibleIngredient, unit: UnitDefinition): ConversionError {
  // Name the volume the owner is thinking in; a mass entered for a liquid asks about a cup.
  const volumeName = unit.kind === 'volume' ? unit.name : 'cup';
  return new ConversionError(
    'DENSITY_REQUIRED',
    `Set how much 1 ${volumeName} of ${nameOf(ingredient)} weighs`,
    'density_g_per_ml',
    unit.code,
  );
}

function eachWeightRequired(
  ingredient: ConvertibleIngredient,
  unit: UnitDefinition,
): ConversionError {
  return new ConversionError(
    'EACH_WEIGHT_REQUIRED',
    `Set how much one ${nameOf(ingredient)} weighs`,
    'each_weight_g',
    unit.code,
  );
}

/**
 * Move an amount expressed in the base of one kind (g, ml or each) to the base of another,
 * through the ingredient's density and each weight. Throws naming the missing bridge.
 */
function bridge(
  amount: number,
  from: UnitKind,
  to: UnitKind,
  ingredient: ConvertibleIngredient,
  unit: UnitDefinition,
): number {
  if (from === to) return amount;
  const density = densityOf(ingredient);
  const each = eachWeightOf(ingredient);
  const needDensity = () => {
    if (density === null) throw densityRequired(ingredient, unit);
    return density;
  };
  const needEach = () => {
    if (each === null) throw eachWeightRequired(ingredient, unit);
    return each;
  };
  if (from === 'mass' && to === 'volume') return amount / needDensity();
  if (from === 'volume' && to === 'mass') return amount * needDensity();
  if (from === 'count' && to === 'mass') return amount * needEach();
  if (from === 'mass' && to === 'count') return amount / needEach();
  // count <-> volume needs both; the each weight is asked for first either way.
  if (from === 'count' && to === 'volume') {
    const grams = amount * needEach();
    return grams / needDensity();
  }
  const gramsPerEach = needEach();
  return (amount * needDensity()) / gramsPerEach;
}

function canBridge(from: UnitKind, to: UnitKind, ingredient: ConvertibleIngredient): boolean {
  if (from === to) return true;
  const hasDensity = densityOf(ingredient) !== null;
  const hasEach = eachWeightOf(ingredient) !== null;
  const pair = new Set([from, to]);
  if (pair.has('mass') && pair.has('volume')) return hasDensity;
  if (pair.has('mass') && pair.has('count')) return hasEach;
  return hasDensity && hasEach; // count <-> volume
}

/**
 * Convert an entered quantity into the ingredient's base unit.
 *
 * @example
 * toBaseQuantity(1, 'cup', { name: 'Honey', base_unit: 'g', density_g_per_ml: 340 / 236.5882365 })
 * // => 340
 * @throws {ConversionError} naming what is missing ("Set how much 1 cup of Honey weighs")
 */
export function toBaseQuantity(
  qty: number,
  unit: QuantityUnit,
  ingredient: ConvertibleIngredient,
): number {
  if (!Number.isFinite(qty) || qty < 0) {
    throw new ConversionError('INVALID_QUANTITY', `Quantity must be zero or more, got ${qty}`);
  }
  if (typeof unit === 'object') {
    const pkg = ingredient.packages?.find((p) => p.id === unit.packageId);
    if (!pkg) {
      throw new ConversionError(
        'UNKNOWN_PACKAGE',
        `That package is not set up for ${nameOf(ingredient)}`,
      );
    }
    const perPackage = Number(pkg.quantity);
    if (!Number.isFinite(perPackage) || perPackage <= 0) {
      throw new ConversionError(
        'INVALID_QUANTITY',
        `Package "${pkg.label}" must hold more than zero`,
      );
    }
    return qty * toBaseQuantity(perPackage, pkg.unit, ingredient);
  }
  const def = getUnit(unit);
  if (!def) throw new ConversionError('UNKNOWN_UNIT', `Unknown unit: ${unit}`, null, unit);
  return bridge(qty * def.toBase, def.kind, kindOfBaseUnit(ingredient.base_unit), ingredient, def);
}

/**
 * Express a base-unit quantity in another unit for this ingredient (the inverse of
 * toBaseQuantity). 340 g of honey in 'cup' is 1.
 */
export function fromBaseQuantity(
  baseQty: number,
  unit: string,
  ingredient: ConvertibleIngredient,
): number {
  const def = getUnit(unit);
  if (!def) throw new ConversionError('UNKNOWN_UNIT', `Unknown unit: ${unit}`, null, unit);
  return (
    bridge(baseQty, kindOfBaseUnit(ingredient.base_unit), def.kind, ingredient, def) / def.toBase
  );
}

/** Whether a unit code can be converted for this ingredient with what is set today. */
export function canConvertUnit(unit: string, ingredient: ConvertibleIngredient): boolean {
  const def = getUnit(unit);
  return def !== undefined && canBridge(def.kind, kindOfBaseUnit(ingredient.base_unit), ingredient);
}

export type UnitsBySystem = Record<UnitSystem, string[]>;

/**
 * Unit codes that convert for this ingredient, grouped by measuring system and in catalog
 * order. A unit shared by systems (oz, lb, each) appears in each of its groups.
 */
export function convertibleUnits(ingredient: ConvertibleIngredient): UnitsBySystem {
  const out: UnitsBySystem = { metric: [], us: [], imperial: [] };
  const base = kindOfBaseUnit(ingredient.base_unit);
  for (const u of [...UNIT_CATALOG].sort((a, b) => a.sortOrder - b.sortOrder)) {
    if (!canBridge(u.kind, base, ingredient)) continue;
    for (const s of u.systems) out[s].push(u.code);
  }
  for (const s of UNIT_SYSTEMS) out[s] = [...new Set(out[s])];
  return out;
}

/** The ingredient's packages whose unit converts today. */
export function convertiblePackages(ingredient: ConvertibleIngredient): IngredientPackage[] {
  return (ingredient.packages ?? []).filter((p) => canConvertUnit(p.unit, ingredient));
}
